import React, { createContext, useState, useMemo, useCallback } from 'react';
import { ItemLeadTime, CompletedItemLeadTime } from '../types';

type RequestedItemsItem = ItemLeadTime | CompletedItemLeadTime;

type DrawerTab = 'chat' | 'quote';

type Perms = {
  canSubmit: boolean;
  canUpdate: boolean;
};

type RequestedItemsContextType = {
  selectedItem: RequestedItemsItem | null;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<RequestedItemsItem | null>
  >;
  selectItemById: (worksheetId: string) => void;
  getNextItem: () => RequestedItemsItem | null;
  getPrevItem: () => RequestedItemsItem | null;
  allItems: RequestedItemsItem[];
  setAllItems: (items: RequestedItemsItem[]) => void;
  isDrawerOpen: boolean;
  openDrawer: (worksheetId: string, tab?: DrawerTab) => void;
  closeDrawer: () => void;
  drawerTab: DrawerTab;
  setDrawerTab: (tab: DrawerTab) => void;
  userId: string;
  setUserId: (userId: string) => void;
  isCompleted: boolean;
  setIsCompleted: (isCompleted: boolean) => void;
  perms: Perms;
  setPerms: (perms: Perms) => void;
  domesticEligible: boolean;
  setDomesticEligible: (eligible: boolean) => void;
};

export const RequestedItemsContext = createContext<
  RequestedItemsContextType | undefined
>(undefined);

type RequestedItemsProviderProps = {
  children: React.ReactNode;
};

export function RequestedItemsProvider({
  children,
}: RequestedItemsProviderProps) {
  const [selectedItem, setSelectedItem] = useState<RequestedItemsItem | null>(
    null,
  );
  const [allItems, setAllItems] = useState<RequestedItemsItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('quote');
  const [userId, setUserId] = useState<string>('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [perms, setPerms] = useState<Perms>({
    canSubmit: false,
    canUpdate: false,
  });
  const [domesticEligible, setDomesticEligible] = useState(false);

  const selectItemById = useCallback(
    (worksheetId: string) => {
      const item = allItems.find((i) => i.worksheet_id === worksheetId);
      if (item) {
        setSelectedItem(item);
      }
    },
    [allItems],
  );

  const openDrawer = useCallback(
    (worksheetId: string, tab: DrawerTab = 'quote') => {
      selectItemById(worksheetId);
      setDrawerTab(tab);
      setIsDrawerOpen(true);
    },
    [selectItemById],
  );

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const getNextItem = useCallback(() => {
    if (!selectedItem || !allItems.length) return null;
    const currentIndex = allItems.findIndex(
      (item) => item.worksheet_id === selectedItem.worksheet_id,
    );
    if (currentIndex === -1) return null;
    return allItems[currentIndex + 1] || null;
  }, [selectedItem, allItems]);

  const getPrevItem = useCallback(() => {
    if (!selectedItem || !allItems.length) return null;
    const currentIndex = allItems.findIndex(
      (item) => item.worksheet_id === selectedItem.worksheet_id,
    );
    if (currentIndex === -1) return null;
    return allItems[currentIndex - 1] || null;
  }, [selectedItem, allItems]);

  const value = useMemo(
    () => ({
      selectedItem,
      setSelectedItem,
      selectItemById,
      allItems,
      setAllItems,
      getNextItem,
      getPrevItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      drawerTab,
      setDrawerTab,
      userId,
      setUserId,
      isCompleted,
      setIsCompleted,
      perms,
      setPerms,
      domesticEligible,
      setDomesticEligible,
    }),
    [
      selectedItem,
      allItems,
      isDrawerOpen,
      drawerTab,
      userId,
      isCompleted,
      perms,
      domesticEligible,
      selectItemById,
      openDrawer,
      closeDrawer,
      getNextItem,
      getPrevItem,
    ],
  );

  return (
    <RequestedItemsContext.Provider value={value}>
      {children}
    </RequestedItemsContext.Provider>
  );
}
